import type { InsightRequest, ReportInsights } from "../types";
import { log } from "../utils/logger";
import {
  buildApiUrl,
  getApiErrorMessage,
} from "./transport";

export type InsightProgressHandler = (stage: string, message: string) => void;

type InsightStreamEvent = {
  type?: unknown;
  stage?: unknown;
  message?: unknown;
  data?: unknown;
};

type InsightStreamParseResult =
  | { ok: true; event: InsightStreamEvent }
  | { ok: false; reason: "invalid_json" | "invalid_event"; preview: string };

type InsightStreamState = {
  finalResult: ReportInsights | null;
  streamError: Error | null;
};

function parseInsightStreamEvent(payload: string): InsightStreamParseResult {
  const preview = payload.slice(0, 160);

  try {
    const parsed = JSON.parse(payload) as unknown;
    if (parsed && typeof parsed === "object") {
      return { ok: true, event: parsed as InsightStreamEvent };
    }
    return { ok: false, reason: "invalid_event", preview };
  } catch {
    return { ok: false, reason: "invalid_json", preview };
  }
}

async function readInsightStream(
  response: Response,
  onStatus?: InsightProgressHandler,
): Promise<ReportInsights> {
  if (!response.body) {
    throw new Error("Insight streaming response did not include a body");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  const streamState: InsightStreamState = {
    finalResult: null,
    streamError: null,
  };
  let streamParseErrorCount = 0;
  let lastMalformedPayloadPreview = "";

  const processBuffer = () => {
    const events = buffer.split("\n\n");
    buffer = events.pop() ?? "";

    for (const chunk of events) {
      const dataLine = chunk
        .split("\n")
        .find((line) => line.startsWith("data:"));

      if (!dataLine) {
        continue;
      }

      const payload = dataLine.replace(/^data:\s*/, "");
      if (!payload.trim()) {
        continue;
      }

      const parsedResult = parseInsightStreamEvent(payload);
      if (!parsedResult.ok) {
        streamParseErrorCount += 1;
        lastMalformedPayloadPreview = parsedResult.preview;
        log.warn(
          "Malformed insight stream event",
          {
            reason: parsedResult.reason,
            preview: parsedResult.preview,
          },
          "api",
        );
        continue;
      }

      const parsed = parsedResult.event;

      if (
        parsed.type === "status" &&
        typeof parsed.stage === "string" &&
        typeof parsed.message === "string"
      ) {
        onStatus?.(parsed.stage, parsed.message);
      } else if (parsed.type === "result" && parsed.data) {
        streamState.finalResult = parsed.data as ReportInsights;
      } else if (parsed.type === "error") {
        streamState.streamError = new Error(
          typeof parsed.message === "string"
            ? parsed.message
            : "Insight streaming failed",
        );
      }
    }
  };

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (value) {
        buffer += decoder.decode(value, { stream: !done });
        processBuffer();
      }

      if (streamState.streamError || streamState.finalResult || done) {
        break;
      }
    }

    if (!streamState.streamError && !streamState.finalResult && buffer) {
      processBuffer();
    }
  } finally {
    reader.releaseLock();
  }

  if (streamState.streamError) {
    throw streamState.streamError;
  }

  if (streamState.finalResult) {
    log.info(
      "Streaming insights completed",
      { insightsCount: streamState.finalResult.insights.length },
      "api",
    );
    return streamState.finalResult;
  }

  if (streamParseErrorCount > 0) {
    throw new Error(
      `Insight stream ended without a result after ${streamParseErrorCount} malformed event${streamParseErrorCount === 1 ? "" : "s"}. Last malformed payload: ${lastMalformedPayloadPreview}`,
    );
  }
  throw new Error("Insight stream ended without a result");
}

/**
 * Generate AI-powered insights from campaign report data.
 *
 * @param request - Insight generation request with report data and options
 * @returns AI-generated insights with metadata
 */
export async function generateInsights(
  request: InsightRequest,
): Promise<ReportInsights> {
  const url = buildApiUrl("/analyze/insights");

  log.info(
    "Requesting AI insights generation",
    {
      model: request.model,
      useCodeInterpreter: request.use_code_interpreter,
      url,
    },
    "api",
  );

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorMessage = await getApiErrorMessage(response);

    log.error(
      "Insight generation failed",
      { status: response.status, errorMessage },
      "api",
    );
    throw new Error(errorMessage);
  }

  const data: ReportInsights = await response.json();

  log.info(
    "AI insights generated successfully",
    {
      insightsCount: data.insights.length,
      generationTime: data.generation_time_seconds,
      modelUsed: data.model_used,
    },
    "api",
  );

  return data;
}

/**
 * Stream AI-powered insights with real-time progress updates.
 *
 * Falls back to the standard insights endpoint if streaming is unavailable.
 */
export async function streamInsights(
  request: InsightRequest,
  onStatus?: InsightProgressHandler,
): Promise<ReportInsights> {
  const url = buildApiUrl("/analyze/insights/stream");

  log.info(
    "Requesting streaming AI insights",
    { model: request.model, url },
    "api",
  );

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorMessage = await getApiErrorMessage(response);

    log.error(
      "Streaming insights failed",
      { status: response.status, errorMessage },
      "api",
    );
    throw new Error(errorMessage);
  }

  if (!response.body) {
    log.warn(
      "Streaming unsupported in this environment. Falling back to standard request.",
      undefined,
      "api",
    );
    return generateInsights(request);
  }

  return readInsightStream(response, onStatus);
}
